import {
    MutableRefObject,
    useEffect,
    useMemo,
    useRef,
} from 'react';

import {
    signIn, confirmSignIn, signOut,
} from 'aws-amplify/auth';

import useMounted from '../useMounted';
import AuthSettingsStore from '../../../stores/AuthSettingsStore';
import UserStore from '../../../stores/UserStore';
import { CheckCodeError, ITimeoutCleaner } from '../../Common/CodeAuthenticator';
import {
    CognitoAuthError,
    OTPAuthType,
    PhoneIdentity,
    SimpleCallback,
} from '@/types/types';
import {
    captureErrorForSentry,
    captureOnce,
    getCognitoErrorName,
    retryRequest,
    SentryAdapter,
    sleep,
} from '../../utils';
import { filterCognitoSignInRetryError, generateCode, trySignUp, getLinkIdFromLocation } from './helpers';
import { isMaskedEmail } from '@/components/utils';
import {
    COGNITO_CALL_RETRIES,
    NETWORK_THROTTLE_USERNAME,
    AUTH_SCHEDULE_INTERVAL,
    MAX_AUTH_SCHEDULE_DURATION_MS,
} from './constants';

export interface OTPFlowCore {
    isMountedRef: MutableRefObject<boolean>;
    checkAuthCode: (
        code: string,
        startLoading: SimpleCallback,
        stopLoading: SimpleCallback,
        onSuccess: SimpleCallback,
    ) => Promise<void>;
    sendAuthCode: (
        username: string, authType: OTPAuthType, phoneIdentity?: PhoneIdentity,
    ) => Promise<string>;
    resendTimerRef: MutableRefObject<ITimeoutCleaner>;
}

const NETWORK_TIMEOUT_MS = 5000;

const buildOtpChallengeClientMetadata = (method: OTPAuthType | null): Record<string, string> => ({
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    method: method ?? '',
});

const useOTPFlow = (
    authSettingsStore: AuthSettingsStore,
    userStore: UserStore,
): OTPFlowCore => {
    const signInScheduleRef = useRef<ReturnType<typeof setInterval>>(null);
    const scheduleStartedAtRef = useRef<number | null>(null);
    const scheduleUsernameRef = useRef<string | null>(null);
    const lastOtpMethodRef = useRef<OTPAuthType | null>(null);
    const resendTimerRef = useRef<ITimeoutCleaner>(null);
    const isMountedRef = useMounted();

    const captureException = useMemo<SentryAdapter>(() => captureOnce(captureErrorForSentry), []);

    /** Pause the interval. Keeps refs so `visibilitychange` can resume. */
    const cancelSignInSchedule = (): void => {
        if (signInScheduleRef.current) {
            clearInterval(signInScheduleRef.current);
            signInScheduleRef.current = null;
        }
    };

    /** Terminate the flow: clears refs so `visibilitychange` cannot resurrect it. */
    const stopSignInSchedule = (): void => {
        cancelSignInSchedule();
        scheduleStartedAtRef.current = null;
        scheduleUsernameRef.current = null;
    };

    /**
     * v6 custom-auth sign-in initiator. v6 tracks the challenge state
     * internally (no returned user object). If a stale sign-in flow is still in
     * progress, or the user is already signed in (e.g. resend/revisit), reset it
     * via `signOut()` and retry once so the fresh challenge can start.
     */
    const startCustomAuthSignIn = async (username: string): Promise<void> => {
        const startFlow = (): Promise<unknown> => signIn({
            username,
            options: { authFlowType: 'CUSTOM_WITHOUT_SRP' },
        });
        try {
            await startFlow();
        } catch (error) {
            const errorName = getCognitoErrorName(error);
            const isFlowConflict = errorName === 'UserAlreadyAuthenticatedException'
                || /already a signin flow/i.test((error as Error)?.message || '');
            if (isFlowConflict) {
                await signOut();
                await startFlow();
                return;
            }
            throw error;
        }
    };

    /**
     * Keepalive tick / eager refresh on `visibilitychange` -> visible.
     * Re-initiates the custom-auth challenge (via `startCustomAuthSignIn`, which
     * resets the in-progress flow with `signOut()` first) to mint a fresh Cognito
     * challenge session. This is required because that session (`AuthSessionValidity`)
     * only lives ~3 min, while the emailed OTP is valid ~60 min: without periodic
     * re-init, a still-valid code submitted after the session expires fails with
     * `NotAuthorizedException`, mis-shown as "Wrong OTP code" (see SPX-3644).
     * A failure must not outlive the flow, so we stop the schedule on error.
     */
    const fakeSignIn = async (username: string): Promise<void> => {
        try {
            await startCustomAuthSignIn(username);
        } catch (error) {
            console.log('error due to otp schedule', error);
            captureException(error, 'useOTPFlow.fakeSignIn');
            stopSignInSchedule();
        }
    };

    /** Start (or resume, idempotently) the keepalive interval. */
    const startInterval = (): void => {
        if (signInScheduleRef.current) return; // already running
        signInScheduleRef.current = setInterval(async () => {
            const startedAt = scheduleStartedAtRef.current;
            if (startedAt && Date.now() - startedAt >= MAX_AUTH_SCHEDULE_DURATION_MS) {
                stopSignInSchedule();
                return;
            }
            const username = scheduleUsernameRef.current;
            if (!username) {
                stopSignInSchedule();
                return;
            }
            await fakeSignIn(username);
        }, AUTH_SCHEDULE_INTERVAL);
    };

    const checkAuthCode = async (
        code: string,
        startLoading: SimpleCallback,
        stopLoading: SimpleCallback,
        onSuccess: SimpleCallback,
    ): Promise<void> => {
        stopSignInSchedule();
        let hasError = false;
        let isWrongCode = false;
        let isSignedIn = false;
        startLoading();
        try {
            const output = await confirmSignIn({
                challengeResponse: code,
                options: {
                    clientMetadata: buildOtpChallengeClientMetadata(lastOtpMethodRef.current),
                },
            });
            isSignedIn = output.isSignedIn;
        } catch (error) {
            console.log('could not send custom challenge answer', error);
            hasError = true;
            isWrongCode = getCognitoErrorName(error) === 'NotAuthorizedException';
            if (!isWrongCode) {
                captureErrorForSentry(error, 'useOTPFlow.checkAuthCode');
            }
        }
        // v6 confirmSignIn returns another custom-challenge step (isSignedIn=false)
        // when the code was wrong but retries remain.
        if (!hasError && !isSignedIn) {
            isWrongCode = true;
            hasError = true;
        }
        if (hasError) {
            stopLoading();
            if (isWrongCode) {
                throw new CheckCodeError('Wrong OTP code');
            } else {
                throw new Error('Could not authenticate user');
            }
        } else {
            await userStore.subscribeOnceOnSetUser();
            stopLoading();
            onSuccess();
        }
    };

    const runSignInSchedule = (username: string): void => {
        scheduleStartedAtRef.current = Date.now();
        scheduleUsernameRef.current = username;
        startInterval();
    };

    const { API } = authSettingsStore;

    /*
     * Pause keepalive when the tab is hidden; on visible, eagerly refresh
     * the Cognito session before restarting. The eager refresh is mandatory:
     * without it, a tab hidden longer than Cognito's ~3-min session lifetime
     * returns with an expired token and the user's first submit fails with
     * `NotAuthorizedException` (mis-shown as "Wrong OTP code").
     */
    useEffect(() => {
        const onVisibilityChange = async (): Promise<void> => {
            if (document.visibilityState === 'hidden') {
                cancelSignInSchedule();
                return;
            }
            const username = scheduleUsernameRef.current;
            if (!username) return; // no active flow
            const startedAt = scheduleStartedAtRef.current;
            if (startedAt && Date.now() - startedAt >= MAX_AUTH_SCHEDULE_DURATION_MS) {
                stopSignInSchedule();
                return;
            }
            await fakeSignIn(username);
            // fakeSignIn may have called stopSignInSchedule() on error; only
            // restart the interval if the flow is still active.
            if (scheduleUsernameRef.current) {
                startInterval();
            }
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange);
            stopSignInSchedule();
        };
    }, []);

    const sendAuthCode = async (
        username: string, authType: OTPAuthType, phoneIdentity?: PhoneIdentity,
    ): Promise<string> => {
        const linkId = getLinkIdFromLocation();
        const pathEmail = (username || '').trim();
        // Cognito username must be cleartext. Do not send a sentinel/mask and
        // expect auth to demask bound recp_email — that was a PII oracle (SPX-6446).
        if (!pathEmail || isMaskedEmail(pathEmail) || !pathEmail.includes('@')) {
            throw new Error('Enter your full email address');
        }
        stopSignInSchedule();
        lastOtpMethodRef.current = authType;
        const resolvedFromAuth = await generateCode(
            API,
            pathEmail.toLowerCase(),
            authType,
            phoneIdentity,
        );
        const usernameLower = (resolvedFromAuth || pathEmail).toLowerCase();
        if (!usernameLower.includes('@') || isMaskedEmail(usernameLower)) {
            throw new Error('Enter your full email address');
        }
        // TODO: remove this check
        const shouldEmulateThrottling = usernameLower.includes(NETWORK_THROTTLE_USERNAME);
        if (shouldEmulateThrottling) {
            await sleep(NETWORK_TIMEOUT_MS);
        }
        await trySignUp(usernameLower, API, { linkId, allowRedirectPathFallback: false });
        if (shouldEmulateThrottling) {
            await sleep(NETWORK_TIMEOUT_MS);
        }
        if (isMountedRef.current) {
            await retryRequest<void>(
                async (): Promise<void> => startCustomAuthSignIn(usernameLower),
                (error: unknown) => filterCognitoSignInRetryError(error as CognitoAuthError),
                COGNITO_CALL_RETRIES,
            );
        }
        if (isMountedRef.current) {
            runSignInSchedule(usernameLower);
        }
        return usernameLower;
    };

    return {
        isMountedRef,
        sendAuthCode,
        checkAuthCode,
        resendTimerRef,
    };
};

export default useOTPFlow;
